import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, ChevronDown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const PLANS = [
    {
        name: "Starter",
        tagline: "For teams validating fit.",
        price: { monthly: 0, annual: 0, custom: "Free" },
        note: "First 200 statements/month, free forever.",
        cta: { label: "Start free", to: "/signup" },
        featured: false,
        features: [
            "Up to 200 statements/month",
            "All 220+ banks",
            "Standard cash-flow metrics",
            "REST API + TypeScript SDK",
            "Email support · 48h",
            "Community Slack",
        ],
    },
    {
        name: "Scale",
        tagline: "For production lenders.",
        price: { monthly: 0.28, annual: 0.22, custom: null },
        priceLabel: "per statement",
        note: "Volume tiers from 1,000 — 500,000 statements/month.",
        cta: { label: "Talk to sales", to: "/contact" },
        featured: true,
        features: [
            "Everything in Starter",
            "Fraud heuristics + risk flags",
            "99.9% uptime SLA",
            "4 typed SDKs (TS, Python, Go, Kotlin)",
            "Dedicated onboarding engineer",
            "Chat support · 4h response",
            "SSO + audit logs",
        ],
    },
    {
        name: "Enterprise",
        tagline: "For regulated lenders at scale.",
        price: { monthly: null, annual: null, custom: "Custom" },
        note: "Annual commitments with in-region deployment.",
        cta: { label: "Contact sales", to: "/contact" },
        featured: false,
        features: [
            "Everything in Scale",
            "In-region dedicated deployment",
            "Bank onboarding in 14 days",
            "Private model fine-tuning",
            "Custom schema extensions",
            "Named CSM · quarterly reviews",
            "99.99% uptime SLA",
            "DPA + custom contracting",
        ],
    },
];

const COMPARE_ROWS = [
    { g: "Volume", rows: [
        ["Statements / month", "200", "1k – 500k", "Unlimited"],
        ["Volume tiering", "—", "Yes", "Custom"],
    ]},
    { g: "Coverage", rows: [
        ["Banks supported", "All 220+", "All 220+", "All + custom onboarding"],
        ["Markets", "5", "5", "5 + expansion on request"],
    ]},
    { g: "Output", rows: [
        ["Cash-flow metrics", "Standard", "Standard + enriched", "Standard + custom"],
        ["Fraud heuristics", "—", "Included", "Included + custom"],
        ["Schema customisation", "—", "—", "Yes"],
    ]},
    { g: "Ops", rows: [
        ["SLA", "Best effort", "99.9%", "99.99%"],
        ["Deployment", "Multi-tenant", "Multi-tenant", "In-region dedicated"],
        ["Support", "Email 48h", "Chat 4h", "Named CSM"],
    ]},
];

const FAQS = [
    {
        q: "Do you charge per API call or per statement?",
        a: "Per statement parsed. A 6-month statement with 1,200 transactions counts as one statement — not 1,200 calls.",
    },
    {
        q: "What's included in the Starter plan?",
        a: "Access to every supported bank, the standard cash-flow schema, and our TypeScript SDK. It's genuinely free for up to 200 statements/month — no card required.",
    },
    {
        q: "How do volume tiers work on Scale?",
        a: "The per-statement price drops progressively at 10k, 50k, 100k and 500k/month. Annual commits lock in the lowest tier you'll hit.",
    },
    {
        q: "Is in-region deployment only on Enterprise?",
        a: "Yes. For multi-tenant Scale customers, data is processed ephemerally in our SEA region. Enterprise gets a dedicated VPC deployment for regulatory needs.",
    },
    {
        q: "Can I change plans later?",
        a: "Any time. Downgrades take effect next billing cycle; upgrades take effect immediately with prorated charges.",
    },
];

export default function Pricing() {
    const [billing, setBilling] = useState("annual");
    const [openFaq, setOpenFaq] = useState(0);

    return (
        <div data-testid="page-pricing">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-hero pointer-events-none" />
                <div className="absolute inset-0 bg-grid-subtle bg-grid-fade opacity-55 pointer-events-none" />
                <div className="relative container max-w-[1240px] mx-auto px-6 pt-20 md:pt-28 pb-10">
                    <div className="max-w-[820px]">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Pricing</p>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-5 font-serif text-[48px] sm:text-[64px] lg:text-[78px] leading-[0.98] tracking-[-0.035em] text-balance"
                        >
                            Pay for what you{" "}
                            <span className="italic">actually parse.</span>
                        </motion.h1>
                        <p className="mt-7 text-[17px] md:text-[19px] leading-[1.6] text-muted-foreground max-w-[620px]">
                            No seat fees. No platform fees. No surprise charges when you hit a usage limit. Just per-statement pricing that drops as you grow.
                        </p>
                    </div>

                    <div className="mt-10 inline-flex items-center gap-1 p-1 rounded-full border border-border bg-card" role="tablist">
                        {[
                            { k: "monthly", l: "Monthly" },
                            { k: "annual", l: "Annual", badge: "Save 22%" },
                        ].map((b) => (
                            <button
                                key={b.k}
                                onClick={() => setBilling(b.k)}
                                data-testid={`billing-toggle-${b.k}`}
                                className={cn(
                                    "inline-flex items-center gap-2 px-5 py-2 rounded-full text-[13px] font-medium transition-all",
                                    billing === b.k
                                        ? "bg-primary text-primary-foreground shadow-sm"
                                        : "text-muted-foreground hover:text-foreground"
                                )}
                            >
                                {b.l}
                                {b.badge && (
                                    <span className={cn(
                                        "text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full",
                                        billing === b.k ? "bg-accent text-accent-foreground" : "bg-accent/20 text-accent-foreground/80"
                                    )}>
                                        {b.badge}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Plans */}
            <section className="relative">
                <div className="container max-w-[1240px] mx-auto px-6 py-10 md:py-16">
                    <div className="grid md:grid-cols-3 gap-5">
                        {PLANS.map((p, i) => {
                            const priceVal = billing === "annual" ? p.price.annual : p.price.monthly;
                            const customLabel = p.price.custom;
                            const featured = p.featured;

                            return (
                                <motion.div
                                    key={p.name}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                                    className={cn(
                                        "relative rounded-3xl p-8 md:p-9 flex flex-col",
                                        featured
                                            ? "bg-surface-deep text-surface-deep-fg border border-surface-deep shadow-elegant lg:-my-4"
                                            : "bg-card border border-border"
                                    )}
                                    data-testid={`plan-card-${p.name.toLowerCase()}`}
                                >
                                    {featured && (
                                        <div className="absolute -top-3 left-8 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent text-accent-foreground text-[10.5px] font-semibold uppercase tracking-[0.14em] shadow">
                                            <Sparkles className="w-3 h-3" /> Most popular
                                        </div>
                                    )}
                                    <p className={cn("text-[12px] uppercase tracking-[0.18em]", featured ? "text-accent" : "text-muted-foreground")}>
                                        {p.name}
                                    </p>
                                    <p className={cn("mt-2 text-[14.5px]", featured ? "text-surface-deep-fg/75" : "text-muted-foreground")}>
                                        {p.tagline}
                                    </p>

                                    <div className="mt-6">
                                        {customLabel !== null && priceVal === null ? (
                                            <p className="font-serif text-[54px] leading-none tracking-[-0.04em]">{customLabel}</p>
                                        ) : customLabel && priceVal === 0 ? (
                                            <p className="font-serif text-[54px] leading-none tracking-[-0.04em]">{customLabel}</p>
                                        ) : (
                                            <div className="flex items-baseline gap-1.5">
                                                <span className="font-serif text-[54px] leading-none tracking-[-0.04em]">
                                                    ${priceVal?.toFixed(2)}
                                                </span>
                                                <span className={cn("text-[13px]", featured ? "text-surface-deep-fg/65" : "text-muted-foreground")}>
                                                    /{p.priceLabel || "statement"}
                                                </span>
                                            </div>
                                        )}
                                        <p className={cn("mt-3 text-[12.5px]", featured ? "text-surface-deep-fg/60" : "text-muted-foreground")}>
                                            {p.note}
                                        </p>
                                    </div>

                                    <ul className={cn("mt-8 space-y-3 flex-1", featured ? "text-surface-deep-fg/85" : "text-foreground/85")}>
                                        {p.features.map((f) => (
                                            <li key={f} className="flex items-start gap-3 text-[14px]">
                                                <Check className={cn("w-4 h-4 mt-0.5 shrink-0", featured ? "text-accent" : "text-primary")} />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <Button
                                        variant={featured ? "lime" : "hero"}
                                        size="lg"
                                        asChild
                                        className="mt-8"
                                    >
                                        <Link to={p.cta.to}>
                                            {p.cta.label} <ArrowUpRight className="w-4 h-4 ml-1" />
                                        </Link>
                                    </Button>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Compare */}
            <section className="relative bg-background-soft/70 border-y border-border">
                <div className="container max-w-[1240px] mx-auto px-6 py-24 md:py-28">
                    <div className="max-w-[680px] mb-12">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Compare</p>
                        <h2 className="mt-4 font-serif text-[38px] md:text-[48px] leading-[1.05] tracking-tight text-balance">
                            Everything in every plan,{" "}
                            <span className="italic">itemised.</span>
                        </h2>
                    </div>

                    <div className="rounded-2xl border border-border bg-card overflow-hidden">
                        <div className="grid grid-cols-12 px-6 py-4 text-[12px] uppercase tracking-wider text-muted-foreground border-b border-border bg-background-soft/60">
                            <div className="col-span-6">Feature</div>
                            <div className="col-span-2">Starter</div>
                            <div className="col-span-2">Scale</div>
                            <div className="col-span-2">Enterprise</div>
                        </div>
                        {COMPARE_ROWS.map((group) => (
                            <div key={group.g}>
                                <div className="grid grid-cols-12 px-6 py-3 bg-primary/[0.03] border-b border-border">
                                    <div className="col-span-12 text-[11px] tracking-[0.2em] uppercase text-primary font-semibold">
                                        {group.g}
                                    </div>
                                </div>
                                {group.rows.map(([label, s, sc, e], i) => (
                                    <div
                                        key={label}
                                        className={cn(
                                            "grid grid-cols-12 px-6 py-4 text-[14px]",
                                            i !== group.rows.length - 1 && "border-b border-border/70"
                                        )}
                                    >
                                        <div className="col-span-6 text-foreground">{label}</div>
                                        <div className="col-span-2 text-muted-foreground">{s}</div>
                                        <div className="col-span-2 text-muted-foreground">{sc}</div>
                                        <div className="col-span-2 text-muted-foreground">{e}</div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="relative">
                <div className="container max-w-[1040px] mx-auto px-6 py-24 md:py-32">
                    <div className="max-w-[680px] mb-12">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">FAQ</p>
                        <h2 className="mt-4 font-serif text-[38px] md:text-[52px] leading-[1.02] tracking-tight text-balance">
                            Pricing, demystified.
                        </h2>
                    </div>
                    <div className="border-t border-border">
                        {FAQS.map((f, i) => {
                            const isOpen = openFaq === i;
                            return (
                                <div key={f.q} className="border-b border-border">
                                    <button
                                        onClick={() => setOpenFaq(isOpen ? -1 : i)}
                                        className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                                        data-testid={`pricing-faq-${i}`}
                                        aria-expanded={isOpen}
                                    >
                                        <span className="font-serif text-[20px] md:text-[22px] tracking-tight text-foreground group-hover:text-primary transition-colors">
                                            {f.q}
                                        </span>
                                        <ChevronDown
                                            className={cn(
                                                "w-5 h-5 shrink-0 text-muted-foreground transition-transform duration-300",
                                                isOpen && "rotate-180 text-primary"
                                            )}
                                        />
                                    </button>
                                    <motion.div
                                        initial={false}
                                        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                        className="overflow-hidden"
                                    >
                                        <p className="pb-6 pr-10 text-[15px] leading-relaxed text-muted-foreground max-w-[720px]">
                                            {f.a}
                                        </p>
                                    </motion.div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
}
